<template lang="pug">
.insights-idle-time-widget.insights-widget(
  v-if="showRvoWidgets"
)
  .widget-wrapper
    .widget-common.widget-small.widget-full-height
      .widget-title.d-flex.align-items-center.justify-content-between
        .d-flex.align-items-baseline
          | {{ title }}
          i.hi.hi-info-filled.ml-5(
            v-tooltip="{ placement: 'top' }"
            :title="'Indicates the average amount of idle time in relation to the average tracked time'"
          )

      .widget-body(:class="bodyClasses")
        template(v-if="isReady")
          template(v-if="!isEmptyWidget")
            //- Standard mode (single gauge)
            .idle-time-content.idle-time-content-standard(v-if="!remoteVsOffice")
              .idle-time-stats.mt-15
                InsightsIdleTimeWidgetStatsItem(
                  :percentage="standardStats.percentage"
                  :hours-duration="standardStats.hoursDuration"
                  label-text="Daily average"
                )

              .idle-time-gauge
                AppChartGaugeSemiCircle(
                  :arcs="[{ value: standardStats.gaugePercentage, tooltip: standardTooltip }]"
                  :width="270"
                  :height="146"
                  :thickness="32"
                )

            //- Remote vs Office mode (dual gauge)
            .idle-time-content(v-else)
              .idle-time-stats
                InsightsIdleTimeWidgetStatsItem(
                  :percentage="remoteStats.percentage"
                  :hours-duration="remoteStats.hoursDuration"
                  label-text="Remote daily average"
                  dot-variant="remote"
                )

                InsightsIdleTimeWidgetStatsItem(
                  :percentage="officeStats.percentage"
                  :hours-duration="officeStats.hoursDuration"
                  label-text="Office daily average"
                  dot-variant="office"
                )

              .idle-time-gauge
                AppChartGaugeSemiCircle(
                  :arcs="[ { value: remoteStats.gaugePercentage, tooltip: remoteTooltip }, { value: officeStats.gaugePercentage, tooltip: officeTooltip }, ]"
                  :width="270"
                  :height="146"
                  :thickness="22"
                )
          InsightsNoData(
            v-else
            :type="emptyReason"
          )
      .widget-footer-bordered(v-if="showWidgetBenchmarks")
        InsightsBenchmarksAverages(
          :averages="benchmarksAverages"
          :pins="benchmarkPins"
          :is-loading="!isReady"
          :max="100"
          :widget-name="title"
        )
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { INSIGHTS } from '~/app_constants';
import { formatDuration } from '~/utils/time_utils';
import { Insights, InsightsTracking } from '~/mixins';
import { mapGetters } from 'vuex';
import InsightsBenchmarksAverages from '~/components/insights/InsightsBenchmarksAverages.vue';
import InsightsIdleTimeWidgetStatsItem from '~/components/insights/InsightsIdleTimeWidgetStatsItem.vue';

export default {
  name: 'InsightsIdleTimeWidget',
  components: {
    InsightsBenchmarksAverages,
    InsightsIdleTimeWidgetStatsItem,
    AppChartGaugeSemiCircle: defineAsyncComponent(() => import('~/components/AppChartGaugeSemiCircle.vue')),
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [Insights, InsightsTracking],
  computed: {
    ...mapGetters({
      widgetData: 'idleTime',
      remoteVsOffice: 'remoteVsOffice',
      showRvoWidgets: 'showRvoWidgets',
      showWidgetBenchmarks: 'showWidgetBenchmarks',
    }),
    title() {
      return 'Idle Time';
    },
    widgetKey() {
      return INSIGHTS.api.idleTime;
    },
    hasRemoteData() {
      return (this.widgetData?.worked_seconds_remote || 0) > 0;
    },
    hasOfficeData() {
      return (this.widgetData?.worked_seconds_on_site || 0) > 0;
    },
    benchmarksAverages() {
      if (this.isEmptyWidget) {
        return {};
      }

      const all = this.widgetData?.benchmarks || {};
      const result = {};

      if (this.hasRemoteData) {
        result.remote = all.remote;
      }
      if (this.hasOfficeData) {
        result.on_site = all.on_site;
      }

      return result;
    },
    benchmarkScores() {
      if (this.isEmptyWidget) {
        return {};
      }

      if (this.remoteVsOffice) {
        const scores = {};

        if (this.hasRemoteData) {
          scores.remote = this.remoteStats.percentage;
        }
        if (this.hasOfficeData) {
          scores.on_site = this.officeStats.percentage;
        }

        return scores;
      }

      return this.standardStats.percentage;
    },
    benchmarkPins() {
      const scores = this.benchmarkScores;

      if (typeof scores === 'object') {
        return Object.entries(scores).map(([key, value]) => ({ key, value }));
      }

      return [{ key: 'standard', value: scores }];
    },
    // Standard mode computed properties
    standardStats() {
      return this.buildStats(this.widgetData?.idle_seconds, this.widgetData?.worked_seconds);
    },
    standardTooltip() {
      return `Idle time: ${this.standardStats.hours}<br>Tracked time: ${this.standardStats.workedHours}`;
    },
    // Remote vs Office mode computed properties
    remoteStats() {
      return this.buildStats(this.widgetData?.idle_seconds_remote, this.widgetData?.worked_seconds_remote);
    },
    officeStats() {
      return this.buildStats(this.widgetData?.idle_seconds_on_site, this.widgetData?.worked_seconds_on_site);
    },
    remoteTooltip() {
      return `Remote idle time: ${this.remoteStats.hours}<br>Remote tracked time: ${this.remoteStats.workedHours}`;
    },
    officeTooltip() {
      return `Office idle time: ${this.officeStats.hours}<br>Office tracked time: ${this.officeStats.workedHours}`;
    },
  },
  methods: {
    buildStats(idleSeconds, workedSeconds) {
      const hasData = workedSeconds != null && workedSeconds > 0;
      const idle = idleSeconds ?? 0;
      const worked = workedSeconds ?? 0;

      let percentage = 0;

      if (worked > 0) {
        percentage = (idle / worked) * 100;
      }
      const roundedPercentage = Math.round(percentage);
      const hours = formatDuration(idle, { showSeconds: 'never', style: 'units' });
      const hoursDuration = formatDuration(idle, { showSeconds: 'never' });
      const workedHours = formatDuration(worked, { showSeconds: 'never', style: 'units' });

      // Gauge percentage (capped at 100 for display)
      const gaugePercentage = Math.min(roundedPercentage, 100);

      return {
        hasData,
        percentage: roundedPercentage,
        hours,
        hoursDuration: hasData ? hoursDuration : '--',
        workedHours,
        gaugePercentage: hasData ? gaugePercentage : 0,
      };
    },
  },
};
</script>
